import { useEffect, useRef } from "react";
import { m, AnimatePresence } from "framer-motion";
import { CalendarDays } from "lucide-react";
import { USAGE_TYPES, isRushDeadline, parseValue, todayUTC } from "@shared/commission";
import { useUIStore } from "@/stores/uiStore";
import { useCommissionStore } from "../../stores/commissionStore";
import { useArtistOptions } from "../../hooks/useArtistOptions";
import { useIntakeCopy } from "../../i18n/useIntakeCopy";
import { Pressable } from "../../motion/primitives";
import { DUR, EASE_ENTER } from "../../motion/tokens";
import { ChipGroup } from "../ChipGroup";
import { InfoTip } from "../InfoTip";
import { StepNav } from "../StepNav";
import type { ContentRating, IntakeArtist, UsageType } from "../../types";

function tomorrowISO() {
  const d = new Date(); d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

export function SceneStep({ artist }: { artist: IntakeArtist }) {
  const { c, fmt } = useIntakeCopy();
  const opts = useArtistOptions(artist)!;
  const s = useCommissionStore();
  const hasConfirmedAdult = useUIStore((u) => u.hasConfirmedAdult);
  const wantsNsfw = useRef(false);

  useEffect(() => {
    if (opts.ratingOptions.length === 1 && s.contentRating !== "sfw") s.setContentRating("sfw");
  }, [opts.ratingOptions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hasConfirmedAdult && wantsNsfw.current) { wantsNsfw.current = false; s.setContentRating("nsfw"); }
  }, [hasConfirmedAdult]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickRating = (r: ContentRating) => {
    if (r === "nsfw" && !useUIStore.getState().hasConfirmedAdult) {
      wantsNsfw.current = true;
      useUIStore.setState({ pendingAdultRating: "nsfw" });
      return;
    }
    s.setContentRating(r);
  };

  const chargesRush = "rush_order" in opts.fees && parseValue(opts.fees.rush_order).kind !== "dont";
  const rush = chargesRush && s.deadlineMode === "date" && isRushDeadline(s.deadline, todayUTC());
  const days = artist.deliveryDays;
  const usualTime = !days ? c.scene.deliveryTime.fallback : days === 1 ? c.scene.deliveryTime.one : fmt(c.scene.deliveryTime.many, { days });
  const feeLabel = (k: string) => (c.fees as Record<string, string>)[k]?.replace(" ({n})", "") ?? k.replace(/_/g, " ");

  return (
    <section className="space-y-8">
      <div>
        <h2 data-step-heading tabIndex={-1} className="font-(family-name:--font-display) font-normal text-3xl text-[var(--color-text)] outline-none">{c.scene.question}</h2>
      </div>

      <div>
        <h3 className="mb-3 text-lg font-medium text-[var(--color-text)]">{c.scene.background}</h3>
        <div className="grid gap-3 sm:grid-cols-3">
          {opts.backgrounds.map((b) => (
            <Pressable key={b} selected={s.backgroundType === b} onClick={() => s.setBackgroundType(b)}
              className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] text-left">
              {artist.samples?.backgrounds?.[b] && (
                <img src={artist.samples.backgrounds[b]!.url.replace("/upload/", "/upload/c_fill,g_auto,w_480,h_300,q_auto,f_auto/")}
                  alt={`${c.scene.bg[b].title} background example by ${artist.name}`} loading="lazy" className="aspect-[16/10] w-full object-cover" />
              )}
              <span className="block px-4 pt-4 font-medium text-[var(--color-text)]">{c.scene.bg[b].title}</span>
              <span className="mt-1 block px-4 pb-4 text-sm text-[var(--color-text-muted)]">{fmt(c.scene.bg[b].body, { artist: artist.name })}</span>
            </Pressable>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-1 flex items-center gap-1">
          <h3 className="text-lg font-medium text-[var(--color-text)]">{c.scene.usage}</h3>
          <InfoTip label={c.scene.usageInfo}>
            <span className="block"><strong>{c.scene.use.personal}:</strong> {c.scene.usageTip.personal}</span>
            <span className="mt-2 block"><strong>{c.scene.use.commercial}:</strong> {c.scene.usageTip.commercial}</span>
          </InfoTip>
        </div>
        <p className="mb-3 text-sm text-[var(--color-text-muted)]">{c.scene.usageHint}</p>
        <ChipGroup<UsageType> single label={c.scene.usage}
          options={USAGE_TYPES.map((u) => ({ id: u, label: c.scene.use[u] }))}
          value={s.usageType ? [s.usageType] : []} onToggle={s.setUsageType} />
      </div>

      {opts.ratingOptions.length > 1 && (
        <div>
          <h3 className="mb-3 text-lg font-medium text-[var(--color-text)]">{c.scene.rating}</h3>
          <ChipGroup<ContentRating> single label={c.scene.rating}
            options={opts.ratingOptions.map((r) => ({ id: r, label: c.scene.ratings[r] }))}
            value={s.contentRating ? [s.contentRating] : []} onToggle={pickRating} />
        </div>
      )}

      <div>
        <div className="mb-3 flex items-center gap-1">
          <h3 className="text-lg font-medium text-[var(--color-text)]">{c.scene.deadline}</h3>
          <InfoTip label={c.scene.deadlineInfo}>
            {fmt(c.scene.deadlineTip, { artist: artist.name, time: usualTime })}
            {chargesRush && <> {c.scene.deadlineTipRush}</>}
          </InfoTip>
        </div>
        <ChipGroup<"none" | "date"> single label={c.scene.deadline}
          options={[{ id: "none", label: c.scene.noRush }, { id: "date", label: c.scene.byDate }]}
          value={[s.deadlineMode]} onToggle={s.setDeadlineMode} />
        <AnimatePresence initial={false}>
          {s.deadlineMode === "date" && (
            <m.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0, transition: { duration: DUR.ui, ease: EASE_ENTER } }} exit={{ opacity: 0 }}
              className="mt-3 flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2">
                <CalendarDays className="h-4 w-4 text-[var(--color-text-muted)]" aria-hidden />
                <input type="date" min={tomorrowISO()} value={s.deadline ?? ""} onChange={(e) => s.setDeadline(e.target.value || null)}
                  className="bg-transparent text-[var(--color-text)] outline-none" aria-label={c.scene.byDate} />
              </label>
              {rush && <span className="text-sm text-[var(--color-warning)]">{c.scene.rushNote}</span>}
            </m.div>
          )}
        </AnimatePresence>
      </div>

      {opts.manualFeeKeys.length > 0 && (
        <div>
          <h3 className="mb-3 text-lg font-medium text-[var(--color-text)]">{c.scene.addons}</h3>
          <ChipGroup label={c.scene.addons}
            options={opts.manualFeeKeys.map((k) => ({ id: k, label: feeLabel(k) }))}
            value={s.manualFees} onToggle={s.toggleManualFee} />
        </div>
      )}

      <StepNav canNext={!!s.usageType && (opts.ratingOptions.length <= 1 || !!s.contentRating) && (s.deadlineMode === "none" || !!s.deadline)} />
    </section>
  );
}
